/**
 * ElevenLabs Voice Picker Component
 * Displays ElevenLabs voices from the catalog for US/non-Indian organizations
 * Fetches voices directly from ElevenLabs API based on selected language
 */

import { useMemo, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Volume2, Loader2, Play, Square } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Supported languages for ElevenLabs
export const ELEVENLABS_LANGUAGES = [
  // Global Languages
  { code: 'en', name: 'English' },
  { code: 'en-in', name: 'Indian English' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pl', name: 'Polish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'sv', name: 'Swedish' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'tr', name: 'Turkish' },
  { code: 'id', name: 'Indonesian' },
  { code: 'fil', name: 'Filipino' },
  { code: 'ms', name: 'Malay' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'th', name: 'Thai' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'fi', name: 'Finnish' },
  { code: 'el', name: 'Greek' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'no', name: 'Norwegian' },
  { code: 'ro', name: 'Romanian' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'hr', name: 'Croatian' },
  { code: 'sk', name: 'Slovak' },
] as const;

export interface Voice {
  voice_id: string;
  name: string;
  category?: string;
  labels?: {
    gender?: string;
    accent?: string;
    age?: string;
    description?: string;
    use_case?: string;
    language?: string;
  };
  preview_url?: string;
}

interface ElevenLabsVoicePickerProps {
  value?: string;
  onChange: (voiceId: string) => void;
  disabled?: boolean;
  language: string; // Required - language code to filter voices for
  savedVoices?: Voice[]; // Optional - if provided, uses saved voices instead of fetching from catalog
}

/**
 * ElevenLabs Voice Picker - displays voices for a specific language
 * If savedVoices is provided, it uses those voices filtered by language (no API call)
 * Otherwise, it fetches ALL available voices from the ElevenLabs catalog
 */
export function ElevenLabsVoicePicker({
  value,
  onChange,
  disabled,
  language,
  savedVoices,
}: ElevenLabsVoicePickerProps) {
  const { toast } = useToast();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Fetch voices for the selected language from ElevenLabs catalog (only if savedVoices not provided)
  const { data: catalogVoices = [], isLoading: voicesLoading } = useQuery<Voice[]>({
    queryKey: ["/api/elevenlabs/voices", language],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/elevenlabs/voices?language=${language}`);
      return res.json();
    },
    staleTime: 300000, // 5 minutes
    enabled: !savedVoices, // Only fetch if savedVoices not provided
  });

  // Use saved voices filtered by language, or catalog voices
  const voices = useMemo(() => {
    if (savedVoices) {
      // Filter saved voices by selected language
      return savedVoices.filter(v => v.labels?.language === language);
    }
    return catalogVoices;
  }, [savedVoices, catalogVoices, language]);

  // Play voice preview
  const handlePlayPreview = async () => {
    if (!value) {
      toast({
        title: "No voice selected",
        description: "Please select a voice first",
        variant: "destructive",
      });
      return;
    }

    // Stop if already playing
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest('POST', '/api/agents/elevenlabs/test-voice', {
        voiceId: value,
        language: language,
      });

      if (!response.ok) {
        throw new Error('Failed to generate voice preview');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      setIsPlaying(true);
      audio.play();

      audio.onended = () => {
        setIsPlaying(false);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setIsPlaying(false);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        toast({
          title: "Preview Failed",
          description: "Could not play voice preview. Please try again.",
          variant: "destructive",
        });
      };
    } catch (error: any) {
      console.error('Failed to play ElevenLabs voice preview:', error);
      toast({
        title: "Preview Failed",
        description: error.message || "Could not play voice preview. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get selected voice details
  const selectedVoice = useMemo(
    () => voices.find((v) => v.voice_id === value) || null,
    [value, voices]
  );

  // Group voices by gender
  const femaleVoices = voices.filter(v => v.labels?.gender?.toLowerCase() === 'female');
  const maleVoices = voices.filter(v => v.labels?.gender?.toLowerCase() === 'male');
  const otherVoices = voices.filter(v => {
    const gender = v.labels?.gender?.toLowerCase();
    return gender !== 'female' && gender !== 'male';
  });

  // Get language display name
  const getLanguageName = (code: string) => {
    return ELEVENLABS_LANGUAGES.find(l => l.code === code)?.name || code.toUpperCase();
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          {voicesLoading ? (
            <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading {getLanguageName(language)} voices...</span>
            </div>
          ) : voices.length === 0 ? (
            <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
              <span className="text-sm text-muted-foreground">No voices available for {getLanguageName(language)}</span>
            </div>
          ) : (
            <Select
              value={value ?? ""}
              onValueChange={onChange}
              disabled={disabled || voicesLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Select a ${getLanguageName(language)} voice...`} />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {/* Female Voices */}
                {femaleVoices.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-pink-50 dark:bg-pink-950/30">
                      Female Voices ({femaleVoices.length})
                    </div>
                    {femaleVoices.map((voice) => (
                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{voice.name}</span>
                          {voice.labels?.accent && (
                            <Badge variant="outline" className="text-xs">
                              {voice.labels.accent}
                            </Badge>
                          )}
                          {voice.labels?.age && (
                            <span className="text-xs text-muted-foreground">
                              {voice.labels.age}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </>
                )}

                {/* Male Voices */}
                {maleVoices.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-blue-50 dark:bg-blue-950/30 mt-1">
                      Male Voices ({maleVoices.length})
                    </div>
                    {maleVoices.map((voice) => (
                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{voice.name}</span>
                          {voice.labels?.accent && (
                            <Badge variant="outline" className="text-xs">
                              {voice.labels.accent}
                            </Badge>
                          )}
                          {voice.labels?.age && (
                            <span className="text-xs text-muted-foreground">
                              {voice.labels.age}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </>
                )}

                {/* Other Voices */}
                {otherVoices.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-gray-50 dark:bg-gray-950/30 mt-1">
                      Other Voices ({otherVoices.length})
                    </div>
                    {otherVoices.map((voice) => (
                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{voice.name}</span>
                          {voice.labels?.accent && (
                            <Badge variant="outline" className="text-xs">
                              {voice.labels.accent}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Preview Button */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handlePlayPreview}
          disabled={!value || isLoading || disabled || voicesLoading}
          title="Preview voice"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPlaying ? (
            <Square className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Selected Voice Details */}
      {selectedVoice && (
        <div className="space-y-1 p-3 rounded-md bg-muted/30 border">
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{selectedVoice.name}</span>
            {selectedVoice.labels?.gender && (
              <Badge variant="outline" className="text-xs capitalize">
                {selectedVoice.labels.gender}
              </Badge>
            )}
          </div>
          {selectedVoice.labels?.description && (
            <p className="text-xs text-muted-foreground">
              {selectedVoice.labels.description}
            </p>
          )}
          {selectedVoice.labels?.use_case && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Best for:</span> {selectedVoice.labels.use_case}
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {voices.length} {getLanguageName(language)} voices available • Powered by ElevenLabs
      </p>
    </div>
  );
}
